import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateVRF from "./createVisaRequestForm";
import { BsCloudDownload } from "react-icons/bs";
import { CgSpinner } from "react-icons/cg";
import { PDFDownloadLink } from "@react-pdf/renderer";
import VrfPdfDownload from "../../DummyPdfs/VRFPDFS/vrfpdfDownload";
import { useDispatch } from "react-redux";
import { setAppError, setBreadCrumb } from "../../../../store/userSlice";
import NodeInstance from "../../../../Global/nodeAxios";
import FinalVRFDownload from "../../DummyPdfs/VRFPDFS/finalVRF";
import VrfConformationpopup from "./Vrf_conformationPopUp";
import { CiEdit } from "react-icons/ci";
import { FcCancel } from "react-icons/fc";
import EditvisaRequest from "./edit_visa_requestform";
import CommonreturnPopUp from "../../../../ReusableComponents/VRFRequestPopUp";
import useServerPagination from "../../../../hooks/useServerPagination";
import PaginationControls from "../../../../components/SmartDataTable/PaginationControls";
import SearchFilter from "../../../../components/SmartDataTable/SearchFilter";
import { formatDate } from "../../../../utils/dateFormat";



const NAV_BASE = "p-2 px-4 rounded-md text-white text-sm font-medium";
const NAV_ACTIVE = { backgroundColor: "#1f2937" };
const NAV_INACTIVE = { backgroundColor: "#ef4444" };
const navLink = (isActive) => ({ className: NAV_BASE, style: isActive ? NAV_ACTIVE : NAV_INACTIVE });

export default function VisaRequestsummary({ titles = [], addOrEdit, setAddOrEdit }) {
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [propId, setPropId] = useState(null);
  const [popUp, setPopUp] = useState(false);
  const [propData, setPropData] = useState(null);
  const [deletepopUp, setDeletePopUp] = useState(false);

  // Server-side pagination — same endpoint as vrflist
  const { data, paginationState, goToPage, changePageSize } = useServerPagination({
    apiEndpoint: "/dashboard/vrf-visa",
    token,
    defaultPageSize: 50,
    searchTerm,
    searchDebounceMs: 300,
  });

  useEffect(() => {
    dispatch(
      setBreadCrumb([
        { label: "VISA REQUEST FORM SUMMARY", link: "/dashboard/visa-request-form" },
        { label: "OPS 1 DESK SUMMARY" },
      ])
    );
  }, [location.pathname, dispatch]);

  const handleCreateVRFRoute = useCallback(() => {
    navigate("/dashboard/visa-request-form/create-vrf");
  }, [navigate]);

  const handleEditVrf = useCallback((id) => {
    navigate("/dashboard/visa-request-form/edit-draft-vrf");
    setPropId(id);
  }, [navigate]);

  const handleDeleteClick = useCallback((id) => {
    setDeletePopUp(true);
    setPropId(id);
  }, []);

  const onStatusChange = useCallback((_id, data) => {
    setPopUp(true);
    setPropData(data);
  }, []);

  const downloadFile = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const response = await NodeInstance.get(`/dashboard/vrf-visa/download`, {
        responseType: "blob",
      });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `VRF-DETAILS`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dispatch(setAppError({ msg: "Excel Downloaded", color: "success" }));
      }
    } catch (err) {
      dispatch(setAppError({ msg: err.message }));
    } finally {
      setDownloadLoading(false);
    }
  }, [dispatch]);

  const getStatusDisplay = useCallback((status) => {
    if (status === "DRAFT VRF CREATED") return "PENDING IN GR DESK";
    if (status === "REFUND INITIATED") return "REFUND";
    if (status === "OPS1 CONFIRMED") return "PROCESS COMPLETED";
    return status;
  }, []);

  if (addOrEdit?.routeName === "create-vrf") {
    return <CreateVRF setAddOrEdit={setAddOrEdit} />;
  }

  if (addOrEdit?.routeName === "edit-draft-vrf") {
    return <EditvisaRequest propId={propId} setAddOrEdit={setAddOrEdit} />;
  }

  return (
    <>
      <div className="p-2 uppercase">

        {/* Toolbar */}
        <div className="flex-wrap gap-2 w-full items-center flex flex-row-reverse mb-4">
          <SearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search across all columns..."
            isLoading={paginationState.isLoading && searchTerm.length > 0}
          />
          <button
            className="p-2 px-4 rounded-md bg-blue disabled:bg-gray-500 text-white min-w-fit text-sm flex items-center gap-2"
            onClick={downloadFile}
            disabled={downloadLoading || paginationState.isLoading}
          >
            {downloadLoading ? <CgSpinner className="animate-spin" /> : null}
            <span>{downloadLoading ? "Downloading..." : "Download VRF"}</span>
          </button>
          <span {...navLink(false)}>VISA RETURN REQUEST</span>
          <button {...navLink(true)} onClick={handleCreateVRFRoute}>CREATE VRF</button>
        </div>

        {/* Scrollable Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
          <table className="min-w-full">
            <thead className="sticky top-0 z-20 bg-white border-b border-slate-200">
              <tr>
                {titles.map((col, i) => (
                  <th
                    key={i}
                    className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {paginationState.isLoading ? (
                <tr>
                  <td colSpan={titles.length} className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <CgSpinner className="animate-spin text-xl text-blue-500" />
                      <span>Loading data...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={titles.length} className="px-6 py-8 text-center text-gray-400">
                    {searchTerm ? `No results found for "${searchTerm}"` : "No Data Found"}
                  </td>
                </tr>
              ) : (
                data.map((listItem, index) => (
                  <tr key={listItem.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                      {(paginationState.currentPage - 1) * paginationState.pageSize + index + 1}
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.vrf_reference_number}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.company_name}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.ajeer}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.sponsor_id}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.Nationality}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.visa_stamp_city}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.visa_profession}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.priority_of_issuance}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{listItem?.total_visa}</td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{getStatusDisplay(listItem?.status)}</td>

                    {/* FINAL VRF CONFIRMATION */}
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                      {listItem.status === "GR APPROVED" ? (
                        <button
                          className="text-red font-semibold"
                          onClick={() => onStatusChange(listItem?.id, listItem)}
                        >
                          CONFIRM
                        </button>
                      ) : listItem.status === "OPS1 CONFIRMED" ? (
                        <span className="text-green-500 font-semibold">CONFIRMED</span>
                      ) : (
                        <span>--------</span>
                      )}
                    </td>

                    {/* VRF APPROVED DATE */}
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                      {listItem.visa_block_expiry_date
                        ? formatDate(listItem.created_at)
                        : "-----"}
                    </td>

                    {/* ACTION — PDF download */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {/* PDF download */}
                        <button
                          className="rounded-md bg-white p-1.5 text-sm text-black border border-gray-400"
                          style={{ cursor: "pointer" }}
                        >
                          {listItem.status === "GR APPROVED" || listItem.status === "OPS1 CONFIRMED" ? (
                            <PDFDownloadLink
                              document={<FinalVRFDownload data={listItem} />}
                              fileName="FINAL_VRF.pdf"
                              onLoadSuccess={() => setDownloadReady(false)}
                            >
                              {({ loading }) => (
                                <button onClick={() => setDownloadReady(true)} disabled={loading || downloadReady}>
                                  <BsCloudDownload size={"1rem"} />
                                </button>
                              )}
                            </PDFDownloadLink>
                          ) : (
                            <PDFDownloadLink
                              document={<VrfPdfDownload data={listItem} />}
                              fileName="DRAFT_VRF.pdf"
                              onLoadSuccess={() => setDownloadReady(false)}
                            >
                              {({ loading }) => (
                                <button onClick={() => setDownloadReady(true)} disabled={loading || downloadReady}>
                                  <BsCloudDownload size={"1rem"} />
                                </button>
                              )}
                            </PDFDownloadLink>
                          )}
                        </button>

                        {/* Edit */}
                        <button
                          className="rounded-md bg-white p-1.5 text-sm text-black border border-gray-500"
                          style={{ width: "2rem", height: "2rem" }}
                          onClick={() => handleEditVrf(listItem.id)}
                          disabled={
                            listItem.status !== "DRAFT VRF CREATED" &&
                            listItem.status !== "FINANCE REJECTED" &&
                            listItem.status !== "OPS REJECTED" &&
                            listItem.status !== "GR REJECTED"
                          }
                        >
                          <CiEdit size={"1rem"} />
                        </button>

                        {/* Delete */}
                        <button
                          className="rounded-md bg-white p-1.5 text-sm text-black border border-gray-500"
                          style={{ width: "2rem", height: "2rem" }}
                          onClick={() => handleDeleteClick(listItem.id)}
                          disabled={
                            listItem.status !== "DRAFT VRF CREATED" &&
                            listItem.status !== "GR REJECTED"
                          }
                        >
                          <FcCancel size={"1rem"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky Pagination Footer */}
        <div className="mt-2">
          <PaginationControls
            currentPage={paginationState.currentPage}
            totalPages={paginationState.totalPages}
            pageSize={paginationState.pageSize}
            totalRecords={paginationState.totalRecords}
            pageSizeOptions={[50, 100, 500, 1000]}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            isLoading={paginationState.isLoading}
          />
        </div>
      </div>

      {popUp && (
        <VrfConformationpopup propData={propData} popUp={popUp} setPopUp={setPopUp} />
      )}
      {deletepopUp && (
        <CommonreturnPopUp
          DeletepopUp={deletepopUp}
          setDeletePopUp={setDeletePopUp}
          propId={propId}
          status={"OPS REJECTED"}
          isOpsDelete={true}
        />
      )}
    </>
  );
}
